/* @flow */
import {
  combineReducers
} from 'redux-immutable';
import {
  Set,
  fromJS,
  Map
} from 'immutable';
import type {
  Action
} from 'trustar/utils/redux-types.js';
import {
  pipe
} from 'ramda';
import isEmpty from 'lodash.isempty';

import {
  defaultReportsSearchPayload,
  defaultEntitiesSearchPayload,
  defaultSearchResults,
  defaultReportResult,
  defaultEntityResult
} from './getters';
import {
  UPDATE_ENTIRE_QUERY_OBJECT,
  SORTER,
} from './constants';
import {
  FETCH_METADATA as FETCH_REPORT_METADATA,
  FETCH_METADATA_SUCCESS,
  FETCH_METADATA_FAILURE,
  FETCH_DATA_ALREADY_CACHED,
  STORE,
  REMOVE_SUCCESS as REPORT_REMOVE_SUCCESS,
} from '../reports/action-types';
import {
  FETCH_INDICATOR_METADATA_SUCCESS,
  FETCH_INDICATOR_METADATA_FAILURE,
  FETCH_INDICATOR_METADATA,
} from '../indicators/action-types';
import {
  SET_VIEW_PANEL,
  EXECUTE_SEARCH,
  SET_SEARCH_TERM,
  SET_SEARCH_OPTIONS,
  FETCH_IOCS,
  FETCH_IOCS_SUCCESS,
  FETCH_IOCS_FAILURE,
  FETCH_REPORTS,
  FETCH_REPORTS_SUCCESS,
  FETCH_REPORTS_FAILURE,
  UPDATE_REPORT_PAGE_FILTERS,
  CLEAR_REPORT_SEARCH_FILTERS,
  SEARCH_REPORTS_FAILURE,
  SEARCH_REPORTS_SUCCESS,
  EXECUTE_REPORTS_SEARCH,
  UPDATE_ENTITY_PAGE_FILTERS,
  CLEAR_ENTITY_SEARCH_FILTERS,
  SEARCH_ENTITIES_FAILURE,
  SEARCH_ENTITIES_SUCCESS,
  EXECUTE_ENTITIES_SEARCH,
  UPDATE_SEARCH_PAGE_FILTERS,
  CLEAR_SEARCH_PAGE_FILTERS,
} from './action-types.js';
import {
  Options,
  type SearchOptionsShape,
  searchResponse,
  type SearchResponse,
  type SearchAction,
  type viewPanelType
} from './types';

const query = (state: string = '', action: Action<string>) => {
  const {
    type,
    payload
  } = action;

  switch (type) {
  case CLEAR_ENTITY_SEARCH_FILTERS:
  case CLEAR_REPORT_SEARCH_FILTERS:
  case CLEAR_SEARCH_PAGE_FILTERS:
    return '';
  case EXECUTE_SEARCH:
  case SET_SEARCH_TERM:
    return payload;
  default:
    return state;
  }
};


const panel = (state: string = 'ALL', action: Action<string>) => {
  const {
    type,
    payload
  } = action;

  switch (type) {
  case SET_VIEW_PANEL:
    return payload;
  default:
    return state;
  }
};

/**
 * Like from JS but will handle converting the Set. Unfortunately I've found the
 * reviver function provided with fromJS to be extremely cumbersome to use.
 */
const fromOptions = pipe(
  fromJS,
  x => x.update('enclaveFilters', x => x ? Set(x) : x)
);

const options = (state: SearchOptionsShape = Options(), action: Action<*>) => {
  const {
    type,
    payload
  } = action;

  switch (type) {
  case SET_SEARCH_OPTIONS:
    return state.merge(fromOptions(payload).delete('page'));
  default:
    return state;
  }
};

const iocs = (state: SearchResponse = searchResponse(), action: Action<*>) => {
  const {
    type,
    payload
  } = action;

  switch (type) {
  case EXECUTE_SEARCH:
    return searchResponse();
  case FETCH_IOCS:
    return state.set('loading', true);
  case FETCH_IOCS_SUCCESS:
    return state
        .set('loading', false)
        .merge(payload.delete('data'))
        .set('data', state.get('data').concat(payload.get('data')));
  case FETCH_IOCS_FAILURE:
    return state
        .set('loading', false)
        .set('error', true);
  default:
    return state;
  }
};

const reports = (state: SearchResponse = searchResponse(), action: Action<*>) => {
  const {
    type,
    payload
  } = action;

  switch (type) {
  case EXECUTE_SEARCH:
    return searchResponse();
  case FETCH_REPORTS:
    return state.set('loading', true);
  case FETCH_REPORTS_SUCCESS:
    const currentReports = state.get('data');
    return state
        .set('loading', false)
        .merge(payload.delete('data'))
        .set('data', currentReports.concat(payload.get('data')));
  case FETCH_REPORTS_FAILURE:
    return state
        .set('loading', false)
        .set('error', true);
  default:
    return state;
  }
};


// Search 2.0 Query Payloads, maybe make this it's own thing
const reportPageQueryRaw = (state: Map<string, any> = defaultReportsSearchPayload, action: SearchAction<*>) => {
  const {
    type,
    payload,
    fields,
  } = action;

  switch (type) {
  case UPDATE_REPORT_PAGE_FILTERS:
    if (!fields || isEmpty(fields)) {
        // Error message for now until better solution
      console.error('Error updating report filter, missing field name or \'all\'');
      return state;
    }

    if (fields === UPDATE_ENTIRE_QUERY_OBJECT) {
      console.warn('Setting entire search payload');
      return payload;
    }

    let newState = state.concat([]);

    fields.forEach((field) => {
      const path = field.split('.');
      newState = newState.setIn(path, payload.getIn(path));
    });

    return newState;
  case CLEAR_REPORT_SEARCH_FILTERS:
    return defaultReportsSearchPayload;
  case '@@redux-form/RESET': // Resetting search bar will clear query field
    const {form = ''} = action.meta || {};
    return form === 'search' ? state.set('query', '') : state;
  default:
    return state;
  }
};

// Search Page Query payload is used just to show filter states and such, the actual filtering
// this payload is then converted to an ioc or report query object
// TODO: Lots of boilerplate on this page, action creators perhaps?
const searchPageQuery = (state: Map<string, any> = defaultReportsSearchPayload, action: SearchAction<*>) => {
  const {
    type,
    payload,
    fields,
  } = action;

  switch (type) {
  case UPDATE_SEARCH_PAGE_FILTERS:
    if (!fields || isEmpty(fields)) {
        // Error message for now until better solution
      console.error('Error updating report filter, missing field name or \'all\'');
      return state;
    }

    if (fields === UPDATE_ENTIRE_QUERY_OBJECT) {
      console.warn('Setting entire search payload');
      return payload;
    }

    let newState = state.concat([]);

    fields.forEach((field) => {
      const path = field.split('.');
      newState = newState.setIn(path, payload.getIn(path));
    });

    return newState;
  case CLEAR_SEARCH_PAGE_FILTERS:
    return defaultReportsSearchPayload;
  case '@@redux-form/RESET': // Resetting search bar will clear query field
    const {form = ''} = action.meta || {};
    return form === 'search' ? state.set('query', '') : state;
  default:
    return state;
  }
};

const searchResults = (state: Map<string, any> = defaultSearchResults, action: Action<*>) => {
  const {
    type,
    payload
  } = action;

  switch (type) {
  case SEARCH_ENTITIES_SUCCESS:
  case SEARCH_REPORTS_SUCCESS:
    const searchType = payload.get('searchType');
    return state.set(searchType, payload);
  case CLEAR_ENTITY_SEARCH_FILTERS:
  case SEARCH_ENTITIES_FAILURE:
    return state.set('entities',defaultEntityResult);
  case CLEAR_REPORT_SEARCH_FILTERS:
  case SEARCH_REPORTS_FAILURE:
    return state.set('reports', defaultReportResult);
  case REPORT_REMOVE_SUCCESS:
    const reportToRemoveGuid = payload;
    return state.updateIn(['reports', 'items'], (items =>  items.filter(report => report.get('guid') !== reportToRemoveGuid)));
  default:
    return state;
  }
};

const entityPageQueryRaw = (state: Map<string, any> = defaultEntitiesSearchPayload, action: SearchAction<*>) => {
  const {
    type,
    payload,
    fields,
  } = action;

  switch (type) {
  case UPDATE_ENTITY_PAGE_FILTERS:
    if (!fields || isEmpty(fields)) {
        // Error message for now until better solution
      console.error('Error updating entity filter, missing field name or \'all\'');
      return state;
    }

    if (fields === UPDATE_ENTIRE_QUERY_OBJECT) {
      console.warn('Setting entire search payload');
      return payload;
    }

    let newState = state.concat([]);

    fields.forEach((field) => {
      const path = field.split('.');
      newState = newState.setIn(path, payload.getIn(path));
    });

    return newState;
  case CLEAR_ENTITY_SEARCH_FILTERS:
    return defaultEntitiesSearchPayload;
  case '@@redux-form/RESET': // Resetting search bar will clear query field
    const {form = ''} = action.meta || {};
    return form === 'search' ? state.set('query', '') : state;
  default:
    return state;
  }
};

const loadingMetadata = (state: boolean = false, action: Action<string>) => {
  switch (action.type) {
  case FETCH_METADATA_SUCCESS:
  case FETCH_METADATA_FAILURE:
  case FETCH_INDICATOR_METADATA_SUCCESS:
  case FETCH_INDICATOR_METADATA_FAILURE:
    return false;
  case FETCH_REPORT_METADATA:
  case FETCH_INDICATOR_METADATA:
    return true;
  default:
      return state;
  }
}

const reportsLoadingAllData = (state: boolean = true, action: Action<string>) => {
  const {
    type
  } = action;

  switch (type) {
  case SEARCH_REPORTS_SUCCESS:
  case SEARCH_REPORTS_FAILURE:
  case FETCH_DATA_ALREADY_CACHED:
    return false;
  case EXECUTE_REPORTS_SEARCH:
    return true;
  default:
    return state;
  }
};


const entitiesLoadingAllData = (state: boolean = true, action: Action<string>) => {
  const {
    type
  } = action;

  switch (type) {
  case SEARCH_ENTITIES_SUCCESS:
  case SEARCH_ENTITIES_FAILURE:
  case FETCH_DATA_ALREADY_CACHED:
    return false;
  case EXECUTE_ENTITIES_SEARCH:
    return true;
  default:
    return state;
  }
};

const currentViewPanel = (state: viewPanelType = 'ENTITIES', action: Action<string>) => {
  const {
    type,
    payload
  } = action;

  switch (type) {
  case SET_VIEW_PANEL:
    return payload;
  default:
    return state;
  }
};

const searchPayloadHasBeenInitialized = (state: boolean = false, action: Action<string>) => {
  const { type } = action;

  switch(type) {
  case UPDATE_SEARCH_PAGE_FILTERS:
  case UPDATE_ENTITY_PAGE_FILTERS:
  case UPDATE_REPORT_PAGE_FILTERS:
    return true;
  default:
    return state;
  }
};


// End Search 2.0

export default combineReducers({
  query,
  options,
  iocs,
  reports,
  reportPageQueryRaw,
  searchResults,
  reportsLoadingAllData,
  entityPageQueryRaw,
  entitiesLoadingAllData,
  currentViewPanel,
  searchPageQuery,
  searchPayloadHasBeenInitialized,
  loadingMetadata,
});



// WEBPACK FOOTER //
// ./src/app/modules/search/stores.js